/* Toolkit de item pro Event Manager — mesma lógica do báu (ItemsView):
   - ícone pelo KIND LIMPO (id & ~0x00FFF000), senão itens com flags não mostram foto;
   - deriva +N / flags dos bits do id (client-side);
   - resolve NOME em lote (backend /resolve-items);
   - hint rico (igual à tela da conta) + campo de kind EDITÁVEL com picker + stepper +N. */
import {
  createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2, Package } from 'lucide-react';
import { API_URL } from '@/shared/services/authServices';
import { searchGameItems, resolveItems, type GameItem, type ResolvedItem } from '@/shared/services/eventManagerService';

/* ---------------- lógica de bits (igual items.py:_row_to_item) ---------------- */
// flags no id: +N=0x3E000, bind conta=0x1000, pcbang=0x40000, bind char=0x80000,
// selado=0x100000, bike coating=0xE00000  => máscara 0x00FFF000.
export const cleanKind = (id: number) => ((id & ~0x00FFF000) >>> 0);
export const upgradeOf = (id: number) => ((id >> 13) & 0x1F);                 // +N nos bits 0x3E000
export const withUpgrade = (id: number, n: number) => ((cleanKind(id) | ((Math.max(0, Math.min(31, n)) & 0x1F) << 13)) >>> 0);
export const itemFlags = (id: number) => ({
  bindAccount: !!(id & 0x1000), pcbang: !!(id & 0x40000),
  bindChar: !!(id & 0x80000), sealed: !!(id & 0x100000),
});

/* ---------------- provider de nomes (resolve em lote, cacheado) ---------------- */
const NamesCtx = createContext<{ map: Record<number, ResolvedItem>; ensure: (ks: number[]) => void }>({ map: {}, ensure: () => {} });

export function ItemNamesProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Record<number, ResolvedItem>>({});
  const mapRef = useRef(map); mapRef.current = map;
  const pending = useRef<Set<number>>(new Set());
  const timer = useRef<any>(null);

  const ensure = useCallback((ks: number[]) => {
    let need = false;
    for (const k of ks) if (k > 0 && !(k in mapRef.current) && !pending.current.has(k)) { pending.current.add(k); need = true; }
    if (!need) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const batch = [...pending.current]; pending.current.clear();
      if (!batch.length) return;
      try {
        const res = await resolveItems(batch);
        setMap(m => { const n = { ...m }; for (const [k, v] of Object.entries(res)) n[Number(k)] = v; return n; });
      } catch { /* ignore */ }
    }, 120);
  }, []);

  const value = useMemo(() => ({ map, ensure }), [map, ensure]);
  return <NamesCtx.Provider value={value}>{children}</NamesCtx.Provider>;
}

/** Nome do item (resolvido em lote). Dispara o fetch ao montar/trocar kind. */
export function useItemName(kind: number): string | undefined {
  const { map, ensure } = useContext(NamesCtx);
  useEffect(() => { if (kind > 0) ensure([kind]); }, [kind, ensure]);
  return map[cleanKind(kind)]?.name ?? map[kind]?.name ?? undefined;
}

/* ---------------- ícone (usa o KIND LIMPO) ---------------- */
export function GameItemIcon({ itemId, size = 44, fill = false }: { itemId: number; size?: number; fill?: boolean }) {
  const clean = cleanKind(itemId);
  const [ok, setOk] = useState(true);
  useEffect(() => { setOk(true); }, [clean]);
  return (
    <div className={`${fill ? 'absolute inset-0 w-full h-full' : 'relative shrink-0 border border-white/[0.05]'} rounded-md bg-[#0a0e14] overflow-hidden`}
         style={fill ? undefined : { width: size, height: size }}>
      {ok && itemId ? (
        <img src={`${API_URL}/admin/item-icon/${clean}.png?v=nobg2`} onError={() => setOk(false)} alt="" loading="lazy"
             className="absolute inset-0 w-full h-full object-contain p-0.5" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/20"><Package size={fill ? 18 : size * 0.4} /></div>
      )}
    </div>
  );
}

/* ---------------- hint rico (conteúdo, igual à tela da conta) ---------------- */
export function ItemHintCard({ kind, option, count }: { kind: number; option?: string; count?: number }) {
  const name = useItemName(kind);
  const up = upgradeOf(kind);
  const f = itemFlags(kind);
  const clean = cleanKind(kind);
  return (
    <div className="w-64 p-3 rounded-xl bg-[#0a1218]/98 border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.55)]">
      <div className="flex items-start gap-3">
        <GameItemIcon itemId={kind} size={52} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {up > 0 && <span className="text-amber-400 font-bold text-sm">+{up}</span>}
            <span className="font-bold text-white text-sm leading-tight">{name || `Item #${clean}`}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-white/45 font-mono flex-wrap">
            <span>#{kind}</span>
            {clean !== kind && <span className="text-white/30">base {clean}</span>}
            {count != null && count > 1 && <span className="text-[#00e1d7]">×{count}</span>}
          </div>
        </div>
      </div>
      {(option && option !== '0') && (
        <div className="mt-2 pt-1.5 border-t border-white/[0.06] text-[10px] font-mono break-all">
          <span className="text-white/30 uppercase tracking-wider">Option:</span> <span className="text-white/60">{option}</span>
        </div>
      )}
      {(up > 0 || f.sealed || f.bindAccount || f.bindChar || f.pcbang) && (
        <div className="mt-2 flex flex-wrap gap-1 text-[9px]">
          {up > 0 && <Tag c="amber">+{up} upgrade</Tag>}
          {f.sealed && <Tag c="amber">Selado</Tag>}
          {f.bindAccount && <Tag c="blue">Bind Conta</Tag>}
          {f.bindChar && <Tag c="violet">Bind Char</Tag>}
          {f.pcbang && <Tag c="white">PCBang</Tag>}
        </div>
      )}
    </div>
  );
}
function Tag({ c, children }: { c: 'amber' | 'blue' | 'violet' | 'white'; children: ReactNode }) {
  const m = { amber: 'text-amber-300 border-amber-500/30 bg-amber-500/10', blue: 'text-blue-300 border-blue-500/30 bg-blue-500/10',
    violet: 'text-violet-300 border-violet-500/30 bg-violet-500/10', white: 'text-white/70 border-white/15 bg-white/[0.05]' }[c];
  return <span className={`px-1.5 py-0.5 rounded border ${m}`}>{children}</span>;
}

/* ---------------- wrapper de hover (portal, não clipa no grid) ---------------- */
export function ItemHover({ kind, option, count, children, className = 'inline-flex' }: { kind: number; option?: string; count?: number; children: ReactNode; className?: string }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect(); if (!r) return;
    let left = r.right + 8, top = r.top;
    if (left + 260 > window.innerWidth - 8) left = r.left - 268;
    if (top + 180 > window.innerHeight - 8) top = window.innerHeight - 188;
    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
  };
  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={() => setPos(null)} className={className}>
      {children}
      {pos && kind > 0 && createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1100, pointerEvents: 'none' }}>
          <ItemHintCard kind={kind} option={option} count={count} />
        </div>, document.body)}
    </span>
  );
}

/* ---------------- campo de kind EDITÁVEL (icon + input + nome + picker + stepper +N) ---------------- */
export function ItemKindField({
  value, onChange, showUpgrade = true, iconSize = 46,
}: { value: number; onChange: (v: number) => void; showUpgrade?: boolean; iconSize?: number }) {
  const [pick, setPick] = useState(false);
  const name = useItemName(value);
  const up = upgradeOf(value);
  return (
    <div className="rounded-lg bg-[#0d141b] border border-white/[0.06] p-2">
      <div className="flex items-center gap-2">
        <ItemHover kind={value} ><GameItemIcon itemId={value} size={iconSize} /></ItemHover>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {up > 0 && <span className="text-amber-400 font-bold text-xs">+{up}</span>}
            <span className="text-sm text-white truncate">{value ? (name || `Item #${cleanKind(value)}`) : 'Escolher item...'}</span>
          </div>
          <input value={value || ''} onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, '')) || 0)}
            placeholder="ItemKindIdx" title="ItemKindIdx (editável — +0/+20 têm ids diferentes)"
            className="mt-1 w-full bg-[#0a0e14] border border-white/[0.06] rounded px-2 py-1 text-[11px] text-white font-mono" />
        </div>
        <button onClick={() => setPick(true)} title="Buscar item"
          className="p-2 rounded-lg bg-white/[0.05] text-white/70 hover:text-[#00e1d7] hover:bg-[#00e1d7]/10 transition cursor-pointer shrink-0">
          <Search size={15} />
        </button>
      </div>
      {showUpgrade && value > 0 && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/[0.05]">
          <span className="text-[10px] text-white/45">Upgrade +N</span>
          <input type="number" min={0} max={20} value={up} onChange={(e) => onChange(withUpgrade(value, Number(e.target.value)))}
            className="w-16 bg-[#0a0e14] border border-white/[0.06] rounded px-2 py-1 text-xs text-white font-mono" />
          <span className="text-[9px] text-white/30 font-mono">→ #{withUpgrade(value, up)}</span>
        </div>
      )}
      <GameItemPicker open={pick} onClose={() => setPick(false)} title="Selecionar item" onPick={(it) => onChange(it.itemId)} />
    </div>
  );
}

/** Kind editável COMPACTO (1 linha: hover-icon + input + nome) pra linhas de pool/preço. */
export function ItemKindInline({ value, onChange, size = 34 }: { value: number; onChange: (v: number) => void; size?: number }) {
  const [pick, setPick] = useState(false);
  const name = useItemName(value);
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <ItemHover kind={value}><GameItemIcon itemId={value} size={size} /></ItemHover>
      <div className="min-w-0 flex-1">
        {name && <p className="text-[11px] text-white/75 truncate leading-tight">{name}</p>}
        <div className="flex items-center gap-1">
          <input value={value || ''} onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, '')) || 0)} placeholder="kind"
            className="w-24 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-0.5 text-[10px] text-white font-mono" />
          <button onClick={() => setPick(true)} title="Buscar" className="text-white/40 hover:text-[#00e1d7] cursor-pointer"><Search size={12} /></button>
        </div>
      </div>
      <GameItemPicker open={pick} onClose={() => setPick(false)} onPick={(it) => onChange(it.itemId)} />
    </div>
  );
}

/* ---------------- picker (busca no catálogo, com ícone masked + colar ID) ---------------- */
export function GameItemPicker({
  open, onClose, onPick, title = 'Selecionar item',
}: { open: boolean; onClose: () => void; onPick: (it: GameItem) => void; title?: string }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<GameItem[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) { setQ(''); setItems([]); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const s = q.trim();
    if (s.length < 2) { setItems([]); return; }
    let alive = true; setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await searchGameItems(s); if (alive) setItems(r); }
      catch { if (alive) setItems([]); }
      finally { if (alive) setLoading(false); }
    }, 280);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  if (!open) return null;
  const pasteId = Number(q.trim());
  const canPasteId = Number.isInteger(pasteId) && pasteId > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh] bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>
        <div className="p-3 border-b border-white/[0.06]">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou colar Item ID..."
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30" />
          </div>
          {canPasteId && (
            <button onClick={() => { onPick({ itemId: pasteId, name: `#${pasteId}` }); onClose(); }}
              className="mt-2 w-full flex items-center gap-3 p-2 rounded-lg bg-[#00e1d7]/10 border border-[#00e1d7]/25 hover:bg-[#00e1d7]/15 transition cursor-pointer text-left">
              <GameItemIcon itemId={pasteId} size={36} />
              <span className="text-sm text-white">Usar Item ID <span className="font-mono text-[#00e1d7]">#{pasteId}</span></span>
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="flex items-center justify-center py-8 text-white/40"><Loader2 size={18} className="animate-spin" /></div>}
          {!loading && q.trim().length >= 2 && items.length === 0 && <p className="text-center text-xs text-white/40 py-8">Nenhum item encontrado</p>}
          {!loading && q.trim().length < 2 && <p className="text-center text-xs text-white/40 py-8">Digite ao menos 2 caracteres</p>}
          <div className="space-y-1">
            {items.map(it => (
              <button key={it.itemId} onClick={() => { onPick(it); onClose(); }}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition cursor-pointer text-left">
                <GameItemIcon itemId={it.itemId} size={40} />
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium truncate">{it.name}</p>
                  <p className="text-[10px] text-white/40 font-mono">#{it.itemId}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
